import { inject } from "@vercel/analytics"
import { injectSpeedInsights } from '@vercel/speed-insights';

import { MAX_SOURCES, MAX_OBSTACLES, MIN_VISIBLE_INTENSITY, MIN_OPACITY_THRESHOLD } from './constants.js'
import { initializeEvents } from './events.js'
import { flattenSources } from './utils/flattenSources.js'
import { flattenObstacles } from './utils/flattenObstacles.js'
import { getRandomlyMovingSource } from "./utils/getRandomlyMovingSource.js"
import { addEvent } from './utils/event.js'
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { globalState } from "./state.js"
import { runPlannedActions, useInterval } from "./utils/useInterval.js"

inject()
injectSpeedInsights();

let isDev = location.hostname === 'localhost' || localStorage.getItem('dev') === 'true';

Object.defineProperty(window, 'dev', {
  get() {
    isDev = true;
    localStorage.setItem('dev', 'true');
  }
})

const devLog = (...args) => {
  if (isDev) {
    console.log(...args);
  }
}

const initEverything = () => {
  const tooltips = [...document.querySelectorAll('[data-tooltip]')];
  tooltips.forEach(tooltip => {
    tippy(tooltip, {
      content: tooltip.getAttribute('data-tooltip'),
      placement: 'right',
    });
  });

  if ('serviceWorker' in navigator && !isDev) {
    navigator.serviceWorker.register(new URL('../sw.js', import.meta.url), {
      scope: '/'
    })
      .catch(error => {
        console.error('Service Worker registration failed:', error);
      });
  }

  const state = {
    isMovingSourceManually: false,
    isPaused: false,
  }

  const pauseButton = document.querySelector('.pauseButton');
  const togglePause = () => {
    state.isPaused = !state.isPaused;
    if (state.isPaused) {
      pauseButton.textContent = 'resume';
    } else {
      pauseButton.textContent = 'pause';
    }
  }
  pauseButton.addEventListener('click', togglePause);

  const GpuConstructor = GPU.GPU ?? GPU;
  if (!GpuConstructor) {
    throw new Error('GPU.js is not loaded properly');
  }

  function ccw(aX, aY, bX, bY, cX, cY) {
    const crossProduct = (cY - aY) * (bX - aX) - (bY - aY) * (cX - aX);
    return crossProduct > 0 ? 1 : 0;
  }

  function isBetween(from, to, value) {
    const smallest = Math.min(from, to);
    const largest = Math.max(from, to);

    if (value >= smallest && value <= largest) {
      return 1;
    }
    
    return 0;
  }

  function intersect(aX, aY, bX, bY, cX, cY, dX, dY) {
    const ACDResult = ccw(aX, aY, cX, cY, dX, dY);
    const BCDResult = ccw(bX, bY, cX, cY, dX, dY);
    
    if (ACDResult === BCDResult) return 1;
    
    const ABCResult = ccw(aX, aY, bX, bY, cX, cY);
    const ABDResult = ccw(aX, aY, bX, bY, dX, dY);
    
    return (ABCResult === ABDResult) ? 1 : 0;
  }

  const gpu = new GpuConstructor();
  const render = gpu.createKernel(function (sources, obstacles) {
    const x = this.thread.x;
    const y = this.thread.y;

    let sumOfIntensitiesR = 0;
    let sumOfIntensitiesG = 0;
    let sumOfIntensitiesB = 0;

    for (let i = 0; i < this.constants.sourcesCount; i++) {
      const startIndex = i * 6;
      const sourceX = sources[startIndex];
      const sourceY = sources[startIndex + 1];
      const sourceIntensity = sources[startIndex + 2];
      
      if (sourceIntensity <= 0) continue;
      
      const sourceColorR = sources[startIndex + 3];
      const sourceColorG = sources[startIndex + 4];
      const sourceColorB = sources[startIndex + 5];

      const distanceSquared = (x - sourceX) ** 2 + (y - sourceY) ** 2;
      const intensity = sourceIntensity / Math.sqrt(distanceSquared);

      if (intensity < this.constants.minVisibleIntensity) continue;

      let opacitiesResult = 1;
      for (let j = 0; j < this.constants.obstaclesCount; j++) {
        const obstacleStartX = obstacles[j * 5];
        const obstacleStartY = obstacles[j * 5 + 1];
        const obstacleEndX = obstacles[j * 5 + 2];
        const obstacleEndY = obstacles[j * 5 + 3];
        
        if (obstacleEndX === obstacleStartX && obstacleEndY === obstacleStartY) continue;

        const isXBetween = isBetween(x, sourceX, obstacleEndX) === 1 || isBetween(x, sourceX, obstacleStartX) === 1;
        const isYBetween = isBetween(y, sourceY, obstacleEndY) === 1 || isBetween(y, sourceY, obstacleStartY) === 1  ;

        if (!isXBetween && !isYBetween) continue;
        
        const obstacleOpacity = obstacles[j * 5 + 4];
        
        if (obstacleOpacity >= 1) continue;

        if (opacitiesResult < this.constants.minOpacityThreshold) break;
        
        if (intersect(sourceX, sourceY, x, y, obstacleStartX, obstacleStartY, obstacleEndX, obstacleEndY) === 0) {
          opacitiesResult *= obstacleOpacity;
        }
      }

      const finalIntensity = intensity * opacitiesResult;
      sumOfIntensitiesR += finalIntensity * sourceColorR;
      sumOfIntensitiesG += finalIntensity * sourceColorG;
      sumOfIntensitiesB += finalIntensity * sourceColorB;
    }

    this.color(sumOfIntensitiesR, sumOfIntensitiesG, sumOfIntensitiesB);
  }, {
    constants: {
      sourcesCount: MAX_SOURCES,
      obstaclesCount: MAX_OBSTACLES,
      minVisibleIntensity: MIN_VISIBLE_INTENSITY,
      minOpacityThreshold: MIN_OPACITY_THRESHOLD,
    }
  })
    .setOutput([window.innerWidth, window.innerHeight])
    .setGraphical(true)
    .addFunction(isBetween, {
      argumentTypes: {
        from: 'Number',
        to: 'Number',
        value: 'Number',
      }, returnType: 'Number'
    })
    .addFunction(ccw, {
      argumentTypes: {
        aX: 'Number',
        aY: 'Number',
        bX: 'Number',
        bY: 'Number',
        cX: 'Number',
        cY: 'Number',
      }, returnType: 'Number'
    })
    .addFunction(intersect, {
      argumentTypes: {
        aX: 'Number',
        aY: 'Number',
        bX: 'Number',
        bY: 'Number',
        cX: 'Number',
        cY: 'Number',
        dX: 'Number',
        dY: 'Number',
      }, returnType: 'Number'
    });

  const canvas = render.canvas;
  document.getElementsByTagName('body')[0].prepend(canvas);

  const sources = [];

  const obstacles = []

  let needToGenerateSource = true;
  const lastProcessingTimes = [];
  const maxProcessingTimes = 50;
  const minProcessingTimesToDecide = 10;
  const processingTimeThreshold = 1000 / 30;

  const sourceGenerationInterval = setInterval(() => {
    if (lastProcessingTimes.length < minProcessingTimesToDecide) {
      devLog('Not enough data to decide');
      return;
    }

    if (sources.length >= MAX_SOURCES) {
      devLog('Max sources reached');
      clearInterval(sourceGenerationInterval);
      needToGenerateSource = false;
      return;
    }

    const averageProcessingTime = lastProcessingTimes.reduce((acc, time) => acc + time, 0) / lastProcessingTimes.length;
    globalState.averageProcessingTime = averageProcessingTime;
    devLog(`Processing time: ${averageProcessingTime.toFixed(4)}ms, max: ${processingTimeThreshold.toFixed(1)}ms`);
    devLog(`Already have ${sources.length} sources`);

    if (averageProcessingTime < processingTimeThreshold) {
      devLog('Processing time is low enough, generating source');
      const source = getRandomlyMovingSource(state, blocks, hexagonBlocks);
      if (source) {
        sources.push(source);
        lastProcessingTimes.length = 0;
      }
    } else {
      devLog('Processing time is too high, skipping source generation');
    }
  }, 2000)

  let prevTime = performance.now();
  let prevJson = null;
  function processFrame() {
    if (needToGenerateSource) {
      const currentTime = performance.now();
      const deltaTime = currentTime - prevTime;
      prevTime = currentTime;

      lastProcessingTimes.push(deltaTime);
      if (lastProcessingTimes.length > maxProcessingTimes) {
        lastProcessingTimes.shift();
      }
    }

    runPlannedActions();

    const filteredSources = sources.filter(source => source.isVisible);
    const filteredObstacles = obstacles.filter(obstacle => obstacle.isVisible);

    const input = flattenSources(filteredSources);
    const obstaclesInput = flattenObstacles(filteredObstacles);

    if (!state.isPaused) {
      const json = JSON.stringify({ input, obstaclesInput });
      if (json !== prevJson) {
        prevJson = json;
        render(input, obstaclesInput);
      }
    }

    requestAnimationFrame(processFrame);
  }

  function draw() {
    requestAnimationFrame(processFrame);
  }

  const wrapDivInHexagonBorders = (div) => {
    const rect = div.getBoundingClientRect();
    const topCorner = {
      x: rect.left + rect.width / 2,
      y: rect.top,
    }
    const rightTopCorner = {
      x: rect.right,
      y: rect.top + rect.height * 0.25,
    }
    const rightBottomCorner = {
      x: rect.right,
      y: rect.top + rect.height * 0.75,
    }
    const leftBottomCorner = {
      x: rect.left,
      y: rect.top + rect.height * 0.75,
    }
    const leftTopCorner = {
      x: rect.left,
      y: rect.top + rect.height * 0.25,
    }
    const bottomCorner = {
      x: rect.left + rect.width / 2,
      y: rect.bottom,
    }

    const lines = [
      {
        isVisible: true,
        opacity: 0,
        startX: topCorner.x,
        startY: window.innerHeight - topCorner.y,
        endX: rightTopCorner.x,
        endY: window.innerHeight - rightTopCorner.y,
      },
      {
        isVisible: true,
        opacity: 0,
        startX: rightTopCorner.x,
        startY: window.innerHeight - rightTopCorner.y,
        endX: rightBottomCorner.x,
        endY: window.innerHeight - rightBottomCorner.y,
      },
      {
        isVisible: true,
        opacity: 0,
        startX: rightBottomCorner.x,
        startY: window.innerHeight - rightBottomCorner.y,
        endX: bottomCorner.x,
        endY: window.innerHeight - bottomCorner.y,
      },
      {
        isVisible: true,
        opacity: 0,
        startX: bottomCorner.x,
        startY: window.innerHeight - bottomCorner.y,
        endX: leftBottomCorner.x,
        endY: window.innerHeight - leftBottomCorner.y,
      },
      {
        isVisible: true,
        opacity: 0,
        startX: leftBottomCorner.x,
        startY: window.innerHeight - leftBottomCorner.y,
        endX: leftTopCorner.x,
        endY: window.innerHeight - leftTopCorner.y,
      },
      {
        isVisible: true,
        opacity: 0,
        startX: leftTopCorner.x,
        startY: window.innerHeight - leftTopCorner.y,
        endX: topCorner.x,
        endY: window.innerHeight -  topCorner.y,
      },
    ]

    lines.forEach(line => {
      obstacles.push(line);
      if (obstacles.length > MAX_OBSTACLES) {
        console.warn('Too many obstacles, need to change limit to ', obstacles.length);
      }
    })
  }

  const wrapDivInBorders = (div) => {
    const opacity = 0;
    const rect = div.getBoundingClientRect();
    const topBorder = {
      isVisible: true,
      opacity,
      startX: rect.left,
      startY: window.innerHeight - rect.top,
      endX: rect.right,
      endY: window.innerHeight - rect.top,
    }

    const rightBorder = {
      isVisible: true,
      opacity,
      startX: rect.right,
      startY: window.innerHeight - rect.bottom,
      endX: rect.right,
      endY: window.innerHeight - rect.top,
    }

    const bottomBorder = {
      isVisible: true,
      opacity,
      startX: rect.left,
      startY: window.innerHeight - rect.bottom,
      endX: rect.right,
      endY: window.innerHeight - rect.bottom,
    }

    const leftBorder = {
      isVisible: true,
      opacity,
      startX: rect.left,
      startY: window.innerHeight - rect.bottom,
      endX: rect.left,
      endY: window.innerHeight - rect.top,
    }

    obstacles.push(topBorder, rightBorder, bottomBorder, leftBorder);
    if (obstacles.length > MAX_OBSTACLES) {
      console.warn('Too many obstacles, need to change limit to ', obstacles.length);
    }
  }

  const blocks = [...document.querySelectorAll('.with-shadow')];
  const hexagonBlocks = [...document.querySelectorAll('.with-hexagon-shadow')];
  
  blocks.forEach(wrapDivInBorders);
  hexagonBlocks.forEach(wrapDivInHexagonBorders);

  const gapRect = document.querySelector('.gap').getBoundingClientRect();
  const gapCenterY = gapRect.top + gapRect.height / 2;

  const initialPositionX = window.innerWidth / 2;
  const initialPositionY = window.innerHeight - gapCenterY;
  const controlledSource = {
    isVisible: true,
    position: {
      x: initialPositionX,
      y: initialPositionY,
    },
    targetPosition: {
      x: initialPositionX,
      y: initialPositionY,
    },
    intensity: 0,
    color: {
      r: 1,
      g: 1,
      b: 1,
    },
  }

  sources.push(controlledSource);

  let isVisible = true;
  addEvent(['mousemove', 'touchmove'], (x, y) => {
    devLog('Mouse moved');
    const isInsideSomeBlock =
      [...blocks, ...hexagonBlocks, pauseButton].some(block => {
        const rect = block.getBoundingClientRect();
        const top = window.innerHeight - rect.top;
        const bottom = window.innerHeight - rect.bottom;
        const left = rect.left;
        const right = rect.right;

        return x > left && x < right && y < top && y > bottom;
      })

    if (isInsideSomeBlock) {
      isVisible = false;
      state.isMovingSourceManually = false;
      controlledSource.targetPosition.x = initialPositionX;
      controlledSource.targetPosition.y = initialPositionY;
      return;
    }
    
    isVisible = true;
    state.isMovingSourceManually = true;
    controlledSource.isVisible = true;
    controlledSource.targetPosition.x = x;
    controlledSource.targetPosition.y = y;
  })

  addEvent(['mouseleave', 'touchend', 'mouseout', 'touchcancel'], () => {
    devLog('Mouse left');
    isVisible = false;
    state.isMovingSourceManually = false;
    controlledSource.targetPosition.x = initialPositionX;
    controlledSource.targetPosition.y = initialPositionY;
  })

  const baseControlledSourceIntensity = 15;
  const maxControlledSourceIntensity = baseControlledSourceIntensity * 1.5;
  const controlledSourceStartupFadeDelay = 2000;
  const controlledSourceStartupFadeDuration = 6000;
  const controlledSourceStartupFadeStartTime = performance.now() + controlledSourceStartupFadeDelay;
  const controlledSourceStartupFadeStep =
    baseControlledSourceIntensity * 14 / controlledSourceStartupFadeDuration;
  const controlledSourceIntensityStep = 0.05;
  useInterval(() => {
    if (state.isPaused) {
      return;
    }

    if (state.isMovingSourceManually) {
      if (controlledSource.intensity < maxControlledSourceIntensity) {
        controlledSource.intensity = Math.min(
          controlledSource.intensity + controlledSourceIntensityStep,
          maxControlledSourceIntensity
        );
      }
      return;
    }

    if (controlledSource.intensity < baseControlledSourceIntensity) {
      if (performance.now() < controlledSourceStartupFadeStartTime) {
        return;
      }
      controlledSource.intensity = Math.min(
        controlledSource.intensity + controlledSourceStartupFadeStep,
        baseControlledSourceIntensity
      );
      return;
    }

    if (controlledSource.intensity > baseControlledSourceIntensity) {
      controlledSource.intensity = Math.max(
        controlledSource.intensity - controlledSourceIntensityStep,
        baseControlledSourceIntensity
      );
    }
  })

  initializeEvents(state, controlledSource);

  draw();

  setTimeout(() => {
    if (isDev) {
      return;
    }
    console.clear();
    `
      Wow, I didn't think someone would actually open the console here.
      
      Hello. My name is Aleksei and I'm a software developer since 2015.
      
      I've always loved to create unusual and interesting projects like these ones:
      - https://light.alekseilaud.dev/ - small light-processing project
      - https://sandbox.alekseilaud.dev/ - not-so-small particles simulation project
      - https://zen.alekseilaud.dev/ - simple zen-garden sandbox
      - https://fluid.alekseilaud.dev/ - fluid simulation project
      - https://evolution.alekseilaud.dev/ - online board game
      (you can tell that for some reason I'm fascinated by creating things that are not usually present in the web)

      I don't always work on such projects, of course. I build regular SPAs (and not so regular ones 😃) for different companies.
      Most of the time I work with these technologies:
      - TypeScript
      - React
      - Node.js
      - MobX and less frequently Redux
      - Canvas, rarely WebGL
      - WebSockets, REST
      - MongoDB, PostgreSQL, MySQL
      
      If you want to get in touch with me, you can find me on:
      - https://github.com/kshshe
      - https://t.me/kshshe (preferred)
      - hello@alekseilaud.dev

      --------------------------------

      By the way, if you were looking for the details about this light system, here they are:
      I used GPU.js (https://gpu.rocks/) to render the view. 
      On each frame I send the sources and the obstacles to the GPU (it looks like [x1, y1, intensity1, x2, y2, intensity2, ...] and so on).
      For each pixel GPU calculates the intensity and the color of the light. To do it, it iterates over all sources and checks the distance to the pixel.
      Then it checks if there are any obstacles between the source and the pixel (if the line between pixel(x,y) and source(x1,y1) intersects with any obstacle line).
      If it does intersect, it lowers the intensity of the light using the obstacle opacity parameter.
      You can find more details here: https://github.com/kshshe/light
    `.trim().split('\n').map(l => l.trim()).forEach(l => {
      console.log(l);
    })
  }, 1000)
}

window.addEventListener('load', initEverything);